import React, { useEffect, useState } from "react";
import { 
    Box, 
    Typography, 
    Button, 
    Modal, 
    TextField,
    Card,
    CardContent,
    IconButton,
    Grid,
    Paper,
    Chip
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
    editRailCalendar,
    getRailCalendars
} from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import AddNewCalendar from "./Modal/AddNewCalendar";
import { toast } from "react-toastify";
import { Modal as ModalAntd } from "antd";
import { format } from "date-fns";
import AccessForbidden from "../../../Access/AccessForbidden";
import { style } from "../style";
import Loading from "../../../../components/Loading/Loading";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

const RailCalendar = () => {
    const { state } = useAuth();
    const [open, setOpen] = useState(false);
    const [calendars, setCalendars] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editData, setEditData] = useState<any>({});

    const getCalendars = async () => {
        setLoading(true);
        const response = await getRailCalendars(state.user?.token);

        if (response?.response?.status === 403) {
            setIsAdmin(true);
        } else {
            setCalendars(response || []);
        }
        setLoading(false);
    };

    const startEditing = (calendar: any) => {
        setEditingId(calendar.id);
        setEditData({
            railYearStart: calendar.railYearStart,
            weeks: calendar.weeks
        });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleEditChange = (field: string, value: any) => {
        setEditData((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const saveEdit = async (calendarId: number) => {
        if (!editData.railYearStart) {
            toast.error("Please select a valid start date");
            return;
        }

        const response = await editRailCalendar(
            state.user?.token,
            calendarId,
            format(new Date(editData.railYearStart), "yyyy-MM-dd"),
            editData.weeks
        );

        if (response?.status === 200) {
            toast.success("Updated successfully!");
            setEditingId(null);
            setEditData({});
            getCalendars();
        } else {
            toast.error("Something went wrong!");
        }
    };

    useEffect(() => {
        getCalendars();
    }, []);

    if (loading) return <Loading />;
    if (isAdmin) return <AccessForbidden />;

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                mb: 4 
            }}>
                <Typography variant="h5" fontWeight="600">
                    Rail Calendars
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => setOpen(true)}
                    sx={{ boxShadow: 1 }}
                >
                    Add New Calendar
                </Button>
            </Box>

            <Grid container spacing={3}>
                {calendars.map(calendar => (
                    <Grid item xs={12} md={6} lg={4} key={calendar.id}>
                        <Card 
                            elevation={1}
                            sx={{
                                height: '100%',
                                border: editingId === calendar.id ? '2px solid #1976d2' : '1px solid #e0e0e0'
                            }}
                        >
                            <CardContent>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    mb: 2,
                                }}>
                                    <Typography variant="subtitle1" fontWeight="500">
                                        Calendar #{format(new Date(calendar.railYearStart), "yyyy")}

                                    </Typography>
                                    
                                    {editingId === calendar.id ? (
                                        <Box>
                                            <IconButton 
                                                size="small" 
                                                onClick={() => saveEdit(calendar.id)}
                                                color="primary"
                                                sx={{ mr: 1 }}
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                            <IconButton 
                                                size="small" 
                                                onClick={cancelEditing}
                                                color="error"
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <IconButton 
                                            size="small" 
                                            onClick={() => startEditing(calendar)}
                                            color="primary"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="caption" color="text.secondary" mr={1}>
                                        Weeks
                                    </Typography>
                                    {editingId === calendar.id ? (
                                        <TextField
                                            type="number"
                                            fullWidth
                                            size="small"
                                            value={editData.weeks || calendar.weeks}
                                            onChange={(e) => handleEditChange('weeks', Number(e.target.value))}
                                            sx={{ mt: 0.5 }}
                                        />
                                    ) : (
                                        <Chip 
                                            label={`${calendar.weeks} weeks`}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            sx={{ mt: 0.5 }}
                                        />
                                    )}
                                </Box>

                                {/* Start Date Display */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Start Date
                                    </Typography>
                                    {editingId === calendar.id ? (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                format="DD/MM/YYYY"
                                                slotProps={{
                                                    textField: { 
                                                        size: "small",
                                                        fullWidth: true
                                                    }
                                                }}
                                                value={editData.railYearStart ? dayjs(editData.railYearStart) : dayjs(calendar.railYearStart)}
                                                onChange={(value) => handleEditChange('railYearStart', value ? value.toDate() : null)}
                                                sx={{ mt: 0.5 }}
                                            />
                                        </LocalizationProvider>
                                    ) : (
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                            {format(new Date(calendar.railYearStart), "dd/MM/yyyy")}
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {calendars.length === 0 && (
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        backgroundColor: 'grey.50'
                    }}
                >
                    <Typography color="text.secondary" gutterBottom>
                        No rail calendars found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Click "Add New Calendar" to create your first calendar
                    </Typography>
                </Paper>
            )}

            <Modal open={open} onClose={() => setOpen(false)}>
                <Box sx={style}>
                    <AddNewCalendar
                        getCalendars={getCalendars}
                        setOpen={setOpen}
                    />
                </Box>
            </Modal>
        </Box>
    );
};

export default RailCalendar;    